import { enqueueSnackbar } from 'notistack';
import { useState, Activity, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IconMail,
  IconPhone,
  IconTrash,
  IconSchool,
} from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomModal from '@/components/CustomModal';
import ProgressBar from '@/components/ProgressBar';
import UserDetailsSkeleton from '@/components/SkeletonLoader/UserDetailsSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import { Switch } from '@/components/Switch';
import { TagCapsule } from '@/components/TagCapsule';
import { SchoolStatusConfig } from '@/constants/constant';
import { ERoleAccess, ESchoolStatus, ERestoreEntityType } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  softdeleteRecord,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import { getTeacherDetails } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import { ITeacherDetails, ISelectedRowValue } from '@/types';
import { getAssetUrl, formatLastActiveAt, getGameTagColorClass } from '@/utils';

const TeacherDetailsModal = ({
  setIsOpen,
  selectedTeacherId,
  onRemoveSubmit,
}: {
  setIsOpen: (open: boolean) => void;
  selectedTeacherId: string | undefined;
  onRemoveSubmit: () => void;
}) => {
  const { hasAccess } = useAccess();

  const navigate = useNavigate();

  const handleLessonNavigation = () => {
    navigate(`/user-management/teacher/${selectedTeacherId}/lesson-list`);
  };

  const { loadingModal, setLoadingModal } = useCommonStore();
  const [teacherDetails, setTeacherDetails] = useState<ITeacherDetails | null>(
    null
  );
  const [isRemoveTeacherModal, setIsRemoveTeacherModal] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] =
    useState<ISelectedRowValue | null>(null);
  const { formatText } = useFormatText();

  const fetchTeacherDetails = async () => {
    if (!selectedTeacherId) return;
    setLoadingModal(true);
    try {
      const response = await getTeacherDetails(selectedTeacherId);
      if (response?.status) {
        const data = response?.data;

        setTeacherDetails(data);
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
    }
  };

  useEffect(() => {
    fetchTeacherDetails();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmRemoveTeacher = async () => {
    setConfirmationLoading(true);
    try {
      const params = {
        id: selectedTeacherId as string,
        record_type: ERestoreEntityType.TEACHER,
      };

      const response = await softdeleteRecord(params);
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setIsOpen(false);
        onRemoveSubmit();
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmationLoading(false);
      setIsRemoveTeacherModal(false);
      setSelectedTeacher(null);
    }
  };

  const handleBlockTeacher = async () => {
    if (!selectedTeacherId) return;
    try {
      const params = {
        id: selectedTeacherId as string,
        record_type: ERestoreEntityType.TEACHER,
        is_block: teacherDetails?.status === ESchoolStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchTeacherDetails();
        onRemoveSubmit();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveTeacher = () => {
    if (!teacherDetails) return;
    setSelectedTeacher({
      id: teacherDetails.id,
      name: teacherDetails.teacher_name,
    });
    setIsRemoveTeacherModal(true);
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <>
      <CustomModal
        setIsOpen={setIsOpen}
        isHeader
        isFooter
        position="right"
        loadingModal={loadingModal}
        heading={formatText('teacher.teacher-details')}
        cancelButtonProps={{
          label: formatText('teacher.actions.remove-teacher'),
          desructive: true,
          icon: <IconTrash size={16} />,
          className: '!text-error-default',
          onClick: () => handleRemoveTeacher(),
        }}
        confirmButtonProps={{
          label: formatText('common.actions.done'),
          variant: 'primary',
          onClick: () => setIsOpen(false),
        }}
      >
        {loadingModal ? (
          <UserDetailsSkeleton />
        ) : (
          <div className="space-y-10 w-full bg-white">
            {/* Profile */}
            <div className="flex flex-col items-center gap-4">
              <AvatarGroupList
                list={[
                  {
                    id: teacherDetails?.id || '',
                    name:
                      teacherDetails?.teacher_name ??
                      formatText('teacher.teacher-avatar'),
                    image: getAssetUrl(teacherDetails?.asset),
                  },
                ]}
                size={100}
                avatarClassName="w-25! h-25! text-4xl!"
                className="rounded-full overflow-hidden"
              />

              <div className="text-2xl font-bold leading-8 text-neutral-primary text-center tracking-[-0.24px]">
                {teacherDetails?.teacher_name}
              </div>

              <div className="flex gap-4">
                <span className="px-2 py-1 rounded-full bg-brand-light-50 text-brand-default text-xs font-semibold">
                  {teacherDetails?.school_details.name}
                </span>
                <span className="px-2 py-1 rounded-full bg-warning-light-100 text-warning-primary text-xs font-semibold">
                  {teacherDetails?.total_students ?? 0}{' '}
                  {formatText('user-management.students')}
                </span>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-1.5">
              <Switch
                id="status"
                checked={teacherDetails?.status === ESchoolStatus.ACTIVE}
                onChange={handleBlockTeacher}
              />
              <span className="text-sm text-neutral-primary leading-5">
                {formatText('user-management.status')}:{' '}
                <span className="font-medium">
                  {teacherDetails?.status !== undefined &&
                    formatText(
                      SchoolStatusConfig[teacherDetails.status]?.label
                    )}
                </span>
              </span>
            </div>

            {/* Contact */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-2 min-w-0">
                <IconMail size={20} className="text-neutral-hover shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-neutral-secondary">
                    {formatText('user-management.email')}
                  </span>
                  <span className="text-base text-neutral-primary wrap-anywhere">
                    {teacherDetails?.email}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 min-w-0">
                <IconPhone size={20} className="text-neutral-hover shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-neutral-secondary">
                    {formatText('user-management.phone-number')}
                  </span>
                  <span className="text-base text-neutral-primary wrap-anywhere">
                    {teacherDetails?.phone_code} {teacherDetails?.phone_number}
                  </span>
                </div>
              </div>
            </div>

            {/* Classes */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-neutral-primary">
                  {formatText('user-management.classes')}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-neutral-light-100 text-xs font-semibold text-neutral-primary">
                  {teacherDetails?.classes?.length
                    ?.toString()
                    ?.padStart(2, '0')}
                </span>
              </div>

              <div className="space-y-4">
                {teacherDetails?.classes.map((cls) => (
                  <div
                    key={cls.id}
                    className="p-4 rounded-2xl border border-neutral-light-200 flex flex-col gap-2"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-base font-semibold text-neutral-primary">
                        {cls.name}
                      </span>
                      <span className="text-base text-neutral-secondary">
                        {cls.updated_at
                          ? formatLastActiveAt(Number(cls.updated_at))
                          : '-'}
                      </span>
                    </div>

                    <span className="px-2 py-1 w-fit rounded-full bg-warning-light-100 text-warning-primary text-xs font-semibold">
                      {cls.total_students ?? 0}{' '}
                      {formatText('user-management.students')}
                    </span>

                    <div className="">
                      <div className="flex justify-between items-center my-2">
                        <span className="text-base text-neutral-secondary">
                          {formatText('user-management.progress')}
                        </span>
                        <span className="text-base font-semibold text-neutral-primary">
                          {cls.progress ?? 0}%
                        </span>
                      </div>
                      <ProgressBar
                        value={cls.progress ?? 0}
                        showPercentage={false}
                        barColor="bg-brand-default"
                        backgroundColor="bg-neutral-light-50"
                        height="h-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lessons Created */}
            <div className="flex flex-col gap-4">
              <span className="text-lg font-semibold text-neutral-primary">
                {formatText('teacher.summary-cards.total-lesson-created')}
              </span>

              <div className="p-4 rounded-2xl border border-neutral-light-200 flex flex-col gap-6">
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <IconSchool
                      size={32}
                      className=" text-white p-2 rounded-lg bg-brand-default"
                    />

                    <span className="text-lg font-semibold text-neutral-primary max-w-75">
                      {teacherDetails?.school_details?.name}
                    </span>
                  </div>
                  {teacherDetails?.school_details?.total_lesson !== undefined &&
                    teacherDetails.school_details.total_lesson > 0 && (
                      <Button
                        variant="outline"
                        label={formatText('teacher.actions.view-lesson-list')}
                        className="text-sm"
                        onClick={handleLessonNavigation}
                      />
                    )}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <SummaryCard
                    label={formatText('teacher.summary-cards.overall-lessons')}
                    value={teacherDetails?.school_details?.total_lesson ?? 0}
                    bg="bg-warning-light-100"
                    text="text-warning-primary"
                    sub="text-warning-secondary"
                    variant="highlight"
                    isLoading={loadingModal}
                  />
                  <SummaryCard
                    label={formatText(
                      'teacher.summary-cards.completed-lessons'
                    )}
                    value={
                      teacherDetails?.school_details?.completed_lessons ?? 0
                    }
                    bg="bg-success-light-50"
                    text="text-green-900"
                    sub="text-success-secondary"
                    variant="highlight"
                    isLoading={loadingModal}
                  />
                </div>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="bg-brand-light-50 rounded-2xl p-4 flex flex-col gap-4">
              <span className="text-lg font-semibold text-neutral-primary">
                {formatText('teacher.summary-cards.performance-summary')}
              </span>

              <div className="flex gap-6">
                <div className="flex-1">
                  <span className="text-base text-neutral-secondary">
                    {formatText('teacher.summary-cards.lesson-completed')}
                  </span>
                  <div className="text-[28px] leading-9 font-semibold text-neutral-primary mt-2">
                    {teacherDetails?.performance_summary?.lesson_completed ?? 0}
                    %
                  </div>
                </div>

                <div className="flex-1">
                  <span className="text-base text-neutral-secondary">
                    {formatText('teacher.summary-cards.student-engagement')}
                  </span>
                  <div className="text-[28px] leading-9 font-semibold text-neutral-primary mt-2">
                    {teacherDetails?.performance_summary?.student_engagement ??
                      0}
                    %
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-base text-neutral-secondary">
                  {formatText('teacher.summary-cards.top-games-used')}
                </span>

                <div className="flex flex-wrap gap-2">
                  {teacherDetails?.performance_summary?.top_games_used
                    ?.length ? (
                    teacherDetails.performance_summary.top_games_used.map(
                      (game, index) => {
                        const suffixParts = [];
                        if (
                          game?.quarters !== null &&
                          game?.quarters !== undefined
                        ) {
                          suffixParts?.push(`Q${game?.quarters}`);
                        }
                        if (
                          game?.rounds !== null &&
                          game?.rounds !== undefined
                        ) {
                          suffixParts?.push(`R${game?.rounds}`);
                        }
                        const label = suffixParts?.length
                          ? `${game?.title} ${suffixParts?.join(' ')}`
                          : game?.title;
                        return (
                          <TagCapsule
                            key={game?.id}
                            value={label}
                            className={`border ${getGameTagColorClass(index)}`}
                          />
                        );
                      }
                    )
                  ) : (
                    <span className="text-base text-neutral-primary">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </CustomModal>
      <Activity mode={isRemoveTeacherModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveTeacherModal}
          setIsOpen={setIsRemoveTeacherModal}
          title="teacher.actions.remove-teacher-title"
          descriptionPrefix="teacher.actions.remove-teacher-prefix"
          descriptionSuffix="teacher.actions.remove-teacher-suffix"
          entityName={selectedTeacher?.name}
          confirmLabel={formatText('teacher.actions.remove-teacher')}
          onConfirm={handleConfirmRemoveTeacher}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>
    </>
  );
};

export default TeacherDetailsModal;
